import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AssignableBusiness } from "./AssignAccountManagerModal";
import { MemberOption } from "./MemberSelectionDropdown";
import { buildMemberColumns } from "./memberColumns";
import { Member } from "./types";
import {
  getApiErrorMessage,
  showTeamManagementErrorToast,
  showTeamManagementSuccessToast,
} from "../../../../shared/lib";
import { useRoles } from "../../../../shared/api/hooks/useRoles";
import { useAdmins } from "../../../../shared/api/hooks/useAdmins";
import { useBusinesses } from "../../../../shared/api/hooks/useBusinesses";
import { usePermissions } from "../../../../shared/api/hooks/usePermissions";
import { useDebouncedValue } from "../../../../hooks/use-debounced-value";
import {
  assignCustomersToAdmin,
  deleteAdminProfile,
  inviteMembers,
  resendInvite,
  reassignCustomers,
  updateAdminRoleAndPermissions,
} from "../../../../shared/api/admins";
import {
  getBufferedPageOffset,
  getBufferedRequestLimit,
  getBufferedRequestPage,
  getUiTotalPages,
} from "../../../../shared/lib/bufferedPagination";
import { RootState } from "../../../../redux/store";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const useMembersSectionController = () => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 400);
  const [sort, setSort] = useState("recent");
  const [roleTypes, setRoleTypes] = useState<string[]>(["all"]);

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedAdditionalPermissions, setSelectedAdditionalPermissions] =
    useState<string[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [isAssignRoleOpen, setIsAssignRoleOpen] = useState(false);
  const [isAssignAccountManagerOpen, setIsAssignAccountManagerOpen] =
    useState(false);
  const [
    selectedBusinessIdsForManagerAssign,
    setSelectedBusinessIdsForManagerAssign,
  ] = useState<string[]>([]);
  const [isAssigningCustomers, setIsAssigningCustomers] = useState(false);
  const [assignCustomersError, setAssignCustomersError] = useState<
    string | null
  >(null);
  const [editingMemberRoles, setEditingMemberRoles] = useState<string[]>([]);
  const [isSavingMemberRoles, setIsSavingMemberRoles] = useState(false);
  const [saveMemberRolesError, setSaveMemberRolesError] = useState<
    string | null
  >(null);
  const [editingMemberPermissions, setEditingMemberPermissions] = useState<
    string[]
  >([]);
  const [isSavingMemberPermissions, setIsSavingMemberPermissions] =
    useState(false);
  const [saveMemberPermissionsError, setSaveMemberPermissionsError] = useState<
    string | null
  >(null);
  const [isMemberPermissionsModalOpen, setIsMemberPermissionsModalOpen] =
    useState(false);
  const [memberPermissionsQuery, setMemberPermissionsQuery] = useState("");
  const [memberExpandedPermissionGroupId, setMemberExpandedPermissionGroupId] =
    useState("");
  const [isMemberFlyinOpen, setIsMemberFlyinOpen] = useState(false);
  const [activeMember, setActiveMember] = useState<Member | null>(null);
  const [removedMemberIds, setRemovedMemberIds] = useState<string[]>([]);
  const [optimisticInvitedMembers, setOptimisticInvitedMembers] = useState<
    Member[]
  >([]);

  const [memberPendingRemoval, setMemberPendingRemoval] =
    useState<Member | null>(null);
  const [isDeleteMemberOpen, setIsDeleteMemberOpen] = useState(false);
  const [isManageMemberCustomersOpen, setIsManageMemberCustomersOpen] =
    useState(false);
  const [isReassignCustomersOpen, setIsReassignCustomersOpen] = useState(false);
  const [bulkReassignmentMemberId, setBulkReassignmentMemberId] = useState("");
  const [bulkReassignmentMemberQuery, setBulkReassignmentMemberQuery] =
    useState("");
  const [isBulkReassignmentDropdownOpen, setIsBulkReassignmentDropdownOpen] =
    useState(false);
  const [
    selectedBusinessesForReassignment,
    setSelectedBusinessesForReassignment,
  ] = useState<string[]>([]);
  const [managedCustomersQuery, setManagedCustomersQuery] = useState("");
  const [selectedReassignmentMemberId, setSelectedReassignmentMemberId] =
    useState("");
  const [selectedReassignmentMemberQuery, setSelectedReassignmentMemberQuery] =
    useState("");
  const [
    isSelectedReassignmentDropdownOpen,
    setIsSelectedReassignmentDropdownOpen,
  ] = useState(false);
  const [reassignedCustomersById, setReassignedCustomersById] = useState<
    Record<string, string>
  >({});
  const [isReassigningDeletionCustomers, setIsReassigningDeletionCustomers] =
    useState(false);
  const [isDeletingMember, setIsDeletingMember] = useState(false);
  const [deleteMemberError, setDeleteMemberError] = useState<string | null>(
    null,
  );
  const [reassignDeletionCustomersError, setReassignDeletionCustomersError] =
    useState<string | null>(null);
  const [isFlyinCustomerReassignOpen, setIsFlyinCustomerReassignOpen] =
    useState(false);
  const [flyinBusinessPendingReassign, setFlyinBusinessPendingReassign] =
    useState<{ id: string; name: string } | null>(null);
  const [flyinReassignMemberId, setFlyinReassignMemberId] = useState("");
  const [flyinReassignMemberQuery, setFlyinReassignMemberQuery] = useState("");
  const [isFlyinReassignDropdownOpen, setIsFlyinReassignDropdownOpen] =
    useState(false);
  const [isReassigningFlyinCustomer, setIsReassigningFlyinCustomer] =
    useState(false);
  const [reassignFlyinCustomerError, setReassignFlyinCustomerError] = useState<
    string | null
  >(null);

  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [permissionQuery, setPermissionQuery] = useState("");
  const [expandedGroupId, setExpandedGroupId] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const membersPerPage =
    useSelector((state: RootState) => state.totalItemPerpage) || 20;
  const membersRequestLimit = getBufferedRequestLimit(membersPerPage);
  const membersRequestPage = getBufferedRequestPage(currentPage);

  const { roles: rolesForUi } = useRoles();
  const selectedRoleIdsForFilter = useMemo(() => {
    if (roleTypes.includes("all") || roleTypes.length === 0) {
      return undefined;
    }

    const selectedRoleSet = new Set(roleTypes);
    const roleIds = rolesForUi
      .filter((roleItem) => selectedRoleSet.has(roleItem.name))
      .map((roleItem) => roleItem.id);

    return roleIds.length > 0 ? roleIds.join(",") : undefined;
  }, [roleTypes, rolesForUi]);
  const {
    permissionGroups,
    isLoading: isPermissionsLoading,
    error: permissionsError,
  } = usePermissions();
  const {
    admins,
    meta: adminsMeta,
    isLoading: isMembersLoading,
    refetch: refetchMembers,
  } = useAdmins({
    q: debouncedQuery.trim() || undefined,
    sort,
    limit: membersRequestLimit,
    page: membersRequestPage,
    roleIds: selectedRoleIdsForFilter,
  });
  const { businesses } = useBusinesses();

  const members = useMemo<Member[]>(() => {
    const removedSet = new Set(removedMemberIds);
    const apiMembers = admins
      .filter((admin) => !removedSet.has(admin._id))
      .map((admin) => {
        const fullName =
          `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim();
        const isPendingInvite = admin.isProfileCompleted === false;
        const name = isPendingInvite
          ? admin.email
          : fullName || admin.email?.split("@")[0] || "Unknown member";
        const roles = admin.roles?.length
          ? admin.roles.map((item) => item.name)
          : admin.role
            ? [admin.role]
            : [];

        return {
          id: admin._id,
          name,
          email: admin.email,
          roles,
          permissions: admin.additionalPermissions ?? [],
          businesses: (admin.businesses ?? []).map((business) => ({
            id: business._id,
            name: business.name ?? "Unnamed customer",
            email: business.email,
          })),
          dateAdded: formatDate(admin.createdAt),
          phone: admin.phoneNumber,
          showStatusDot: admin.isProfileCompleted === false,
        };
      });

    const mergedMembers = [...optimisticInvitedMembers];
    const existingEmails = new Set(
      mergedMembers.map((member) => member.email.toLowerCase()),
    );

    apiMembers.forEach((member) => {
      const emailKey = member.email.toLowerCase();
      if (existingEmails.has(emailKey)) return;
      mergedMembers.push(member);
    });

    return mergedMembers;
  }, [admins, optimisticInvitedMembers, removedMemberIds]);

  useEffect(() => {
    if (optimisticInvitedMembers.length === 0) return;

    const apiEmailSet = new Set(admins.map((admin) => admin.email.toLowerCase()));
    setOptimisticInvitedMembers((prev) => {
      const next = prev.filter(
        (member) => !apiEmailSet.has(member.email.toLowerCase()),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [admins, optimisticInvitedMembers.length]);

  const sortOptions = [
    { value: "recent", label: "Recently added" },
    { value: "oldest", label: "Oldest added" },
    { value: "a-z", label: "A - Z" },
    { value: "z-a", label: "Z - A" },
  ];

  const roleTypeOptions = [
    { value: "all", label: "All" },
    ...rolesForUi.map((roleItem) => ({
      value: roleItem.name,
      label: roleItem.name,
    })),
  ];

  const filteredMembers = useMemo(() => {
    return members;
  }, [members]);

  const totalMemberPages = getUiTotalPages(
    adminsMeta,
    membersPerPage,
    membersRequestLimit,
    filteredMembers.length,
  );

  const pagedMembers = useMemo(() => {
    const start = getBufferedPageOffset(currentPage, membersPerPage);
    return filteredMembers.slice(start, start + membersPerPage);
  }, [currentPage, filteredMembers, membersPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery, sort, roleTypes]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalMemberPages));
  }, [totalMemberPages]);

  const rolePermissions = useMemo(() => {
    const selectedRoleSet = new Set(selectedRoles);
    const mappedPermissions = rolesForUi
      .filter((roleItem) => selectedRoleSet.has(roleItem.name))
      .flatMap((roleItem) => roleItem.permissions ?? []);

    return Array.from(new Set(mappedPermissions));
  }, [rolesForUi, selectedRoles]);

  const selectedRoleIds = useMemo(() => {
    const selectedRoleSet = new Set(selectedRoles);
    return rolesForUi
      .filter((roleItem) => selectedRoleSet.has(roleItem.name))
      .map((roleItem) => roleItem.id);
  }, [rolesForUi, selectedRoles]);

  const selectedPermissionsForInvite = useMemo(() => {
    return Array.from(
      new Set([...rolePermissions, ...selectedAdditionalPermissions]),
    );
  }, [rolePermissions, selectedAdditionalPermissions]);

  const editingMemberRoleIds = useMemo(() => {
    const selectedRoleSet = new Set(editingMemberRoles);
    return rolesForUi
      .filter((roleItem) => selectedRoleSet.has(roleItem.name))
      .map((roleItem) => roleItem.id);
  }, [editingMemberRoles, rolesForUi]);

  const rolesForAssignRoleModal = useMemo(() => {
    const byName = new Map<string, (typeof rolesForUi)[number]>();

    rolesForUi.forEach((role) => {
      byName.set(role.name.trim().toLowerCase(), role);
    });

    (activeMember?.roles ?? []).forEach((roleName) => {
      const key = roleName.trim().toLowerCase();
      if (byName.has(key)) return;
      byName.set(key, {
        id: `fallback-${key}`,
        name: roleName,
        description: "",
        permissions: [],
        admins: [],
      });
    });

    return Array.from(byName.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [activeMember?.roles, rolesForUi]);

  const assignableBusinesses = useMemo<AssignableBusiness[]>(() => {
    return businesses
      .map((business) => ({
        id: business._id,
        name: business.name ?? "Unnamed customer",
        email: business.email,
        user: business.user,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [businesses]);

  const unassignedBusinessesForActiveMember = useMemo(() => {
    if (!activeMember) return assignableBusinesses;
    const assignedIds = new Set(
      activeMember.businesses.map((business) => business.id),
    );
    return assignableBusinesses.filter(
      (business) => !assignedIds.has(business.id),
    );
  }, [activeMember, assignableBusinesses]);

  const pendingRemovalBusinesses = useMemo<AssignableBusiness[]>(() => {
    if (!memberPendingRemoval) return [];
    return memberPendingRemoval.businesses.map((business) => ({
      id: business.id,
      name: business.name,
      email: business.email,
    }));
  }, [memberPendingRemoval]);

  const remainingPendingRemovalBusinessIds = useMemo(() => {
    if (!memberPendingRemoval) return [];
    return memberPendingRemoval.businesses
      .filter((business) => !reassignedCustomersById[business.id])
      .map((business) => business.id);
  }, [memberPendingRemoval, reassignedCustomersById]);

  const remainingPendingRemovalCustomersCount =
    remainingPendingRemovalBusinessIds.length;

  const canDeletePendingMember = useMemo(() => {
    if (!memberPendingRemoval) return false;
    if (memberPendingRemoval.businesses.length === 0) return true;
    if (bulkReassignmentMemberId) return true;
    return remainingPendingRemovalCustomersCount === 0;
  }, [
    bulkReassignmentMemberId,
    memberPendingRemoval,
    remainingPendingRemovalCustomersCount,
  ]);

  const toInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? "";
    const second = parts[1]?.charAt(0) ?? "";
    return `${first}${second}`.toUpperCase() || "NA";
  };

  const memberOptionsForReassignment = useMemo<MemberOption[]>(() => {
    if (!memberPendingRemoval) return [];
    return members
      .filter((member) => member.id !== memberPendingRemoval.id)
      .map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        label: toInitials(member.name),
      }));
  }, [memberPendingRemoval, members]);

  const memberOptionsForFlyinReassignment = useMemo<MemberOption[]>(() => {
    if (!activeMember) return [];
    return members
      .filter((member) => member.id !== activeMember.id)
      .map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        label: toInitials(member.name),
      }));
  }, [activeMember, members]);

  const filteredPermissionGroups = useMemo(() => {
    if (permissionGroups.length === 0) return [];
    if (!permissionQuery) return permissionGroups;

    const loweredQuery = permissionQuery.toLowerCase();
    return permissionGroups
      .map((group) => {
        const matchesGroup = group.label.toLowerCase().includes(loweredQuery);
        const matchedPermissions = group.permissions.filter((permission) =>
          permission.toLowerCase().includes(loweredQuery),
        );

        if (!matchesGroup && matchedPermissions.length === 0) return null;
        return {
          ...group,
          permissions: matchesGroup ? group.permissions : matchedPermissions,
        };
      })
      .filter(Boolean) as typeof permissionGroups;
  }, [permissionGroups, permissionQuery]);

  const filteredPermissionGroupsForMember = useMemo(() => {
    if (permissionGroups.length === 0) return [];
    if (!memberPermissionsQuery) return permissionGroups;

    const loweredQuery = memberPermissionsQuery.toLowerCase();
    return permissionGroups
      .map((group) => {
        const matchesGroup = group.label.toLowerCase().includes(loweredQuery);
        const matchedPermissions = group.permissions.filter((permission) =>
          permission.toLowerCase().includes(loweredQuery),
        );

        if (!matchesGroup && matchedPermissions.length === 0) return null;
        return {
          ...group,
          permissions: matchesGroup ? group.permissions : matchedPermissions,
        };
      })
      .filter(Boolean) as typeof permissionGroups;
  }, [permissionGroups, memberPermissionsQuery]);

  const toggleInvitePermission = (permission: string) => {
    if (rolePermissions.includes(permission)) return;
    setSelectedAdditionalPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((item) => item !== permission)
        : [...prev, permission],
    );
  };

  const toggleInviteGroupPermissions = (groupPermissions: string[]) => {
    const togglablePermissions = groupPermissions.filter(
      (permission) => !rolePermissions.includes(permission),
    );
    if (togglablePermissions.length === 0) return;

    const isAllTogglableSelected = togglablePermissions.every((permission) =>
      selectedAdditionalPermissions.includes(permission),
    );

    if (isAllTogglableSelected) {
      setSelectedAdditionalPermissions((prev) =>
        prev.filter((permission) => !togglablePermissions.includes(permission)),
      );
      return;
    }

    setSelectedAdditionalPermissions((prev) => {
      const next = new Set(prev);
      togglablePermissions.forEach((permission) => next.add(permission));
      return Array.from(next);
    });
  };

  const canInviteMembers = emails.length > 0 && selectedRoleIds.length > 0;

  const getInviteErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to invite members. Please try again.");
  };

  const getSaveMemberRolesErrorMessage = (error: unknown) => {
    return getApiErrorMessage(
      error,
      "Unable to update member roles. Please try again.",
    );
  };

  const getAssignCustomersErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to assign customers. Please try again.");
  };

  const getSaveMemberPermissionsErrorMessage = (error: unknown) => {
    return getApiErrorMessage(
      error,
      "Unable to update member permissions. Please try again.",
    );
  };

  const getReassignCustomersErrorMessage = (error: unknown) => {
    return getApiErrorMessage(
      error,
      "Unable to reassign customers. Please try again.",
    );
  };

  const getDeleteMemberErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to delete member. Please try again.");
  };

  const showErrorToast = (message: string) => {
    showTeamManagementErrorToast(message);
  };

  const openInviteModal = () => {
    setEmailInput("");
    setEmails([]);
    setSelectedRoles([]);
    setSelectedAdditionalPermissions([]);
    setPermissionQuery("");
    setExpandedGroupId("");
    setInviteError(null);
    setIsInviteOpen(true);
  };

  const handleInviteMembers = async () => {
    if (!canInviteMembers || isInviting) return;

    const inviteEmails = [...emails];
    const inviteRoles = [...selectedRoles];
    const inviteAdditionalPermissions = [...selectedAdditionalPermissions];

    setInviteError(null);
    setIsInviting(true);
    try {
      await inviteMembers({
        emails: inviteEmails,
        roleIds: selectedRoleIds,
        additionalPermissions: selectedPermissionsForInvite,
      });

      setOptimisticInvitedMembers((prev) => {
        const existingEmails = new Set(prev.map((member) => member.email.toLowerCase()));
        const apiEmails = new Set(admins.map((admin) => admin.email.toLowerCase()));
        const nowIso = new Date().toISOString();
        const pendingInvites = inviteEmails
          .filter((email) => {
            const key = email.toLowerCase();
            return !existingEmails.has(key) && !apiEmails.has(key);
          })
          .map((email, index) => ({
            id: `temp-invite-${Date.now()}-${index}`,
            name: email,
            email,
            roles: inviteRoles,
            permissions: inviteAdditionalPermissions,
            businesses: [],
            dateAdded: formatDate(nowIso),
            showStatusDot: true,
          }));

        return [...pendingInvites, ...prev];
      });

      setIsInviteOpen(false);
      setEmailInput("");
      setEmails([]);
      setSelectedRoles([]);
      setSelectedAdditionalPermissions([]);
      setPermissionQuery("");
      setExpandedGroupId("");
      await refetchMembers();
      showTeamManagementSuccessToast("Invite sent successfully!");
    } catch (error) {
      const message = getInviteErrorMessage(error);
      setInviteError(message);
      showErrorToast(message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvite = useCallback(async (member: Member) => {
    try {
      await resendInvite(member.id);

      showTeamManagementSuccessToast("Invite resent successfully!");
    } catch (error) {
      showTeamManagementErrorToast(getInviteErrorMessage(error));
    }
  }, []);

  const toggleRoleSelection = (roleLabel: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleLabel)
        ? prev.filter((item) => item !== roleLabel)
        : [...prev, roleLabel],
    );
  };

  const toggleEditingMemberRole = (roleLabel: string) => {
    setEditingMemberRoles((prev) =>
      prev.includes(roleLabel)
        ? prev.filter((item) => item !== roleLabel)
        : [...prev, roleLabel],
    );
  };

  const openMemberFlyin = useCallback((member: Member) => {
    setActiveMember(member);
    setIsMemberFlyinOpen(true);
  }, []);

  const openAssignAccountManagerForMember = useCallback((member: Member) => {
    setActiveMember(member);
    setSelectedBusinessIdsForManagerAssign([]);
    setAssignCustomersError(null);
    setIsMemberFlyinOpen(false);
    setIsAssignAccountManagerOpen(true);
  }, []);

  const openEditRolesForMember = useCallback((member: Member) => {
    setActiveMember(member);
    setEditingMemberRoles(member.roles);
    setSaveMemberRolesError(null);
    setIsMemberFlyinOpen(false);
    setIsAssignRoleOpen(true);
  }, []);

  const openAddPermissionsForMember = useCallback((member: Member) => {
    setActiveMember(member);
    setEditingMemberPermissions(member.permissions);
    setMemberPermissionsQuery("");
    setMemberExpandedPermissionGroupId("");
    setSaveMemberPermissionsError(null);
    setIsMemberFlyinOpen(false);
    setIsMemberPermissionsModalOpen(true);
  }, []);

  const openChangeCustomerAccountManager = (businessId: string) => {
    if (!activeMember) return;
    const business = activeMember.businesses.find(
      (item) => item.id === businessId,
    );
    if (!business) return;

    setFlyinBusinessPendingReassign({ id: business.id, name: business.name });
    setFlyinReassignMemberId("");
    setFlyinReassignMemberQuery("");
    setIsFlyinReassignDropdownOpen(false);
    setReassignFlyinCustomerError(null);
    setIsMemberFlyinOpen(false);
    setIsFlyinCustomerReassignOpen(true);
  };

  const resetFlyinCustomerReassignFlow = () => {
    setIsFlyinCustomerReassignOpen(false);
    setFlyinBusinessPendingReassign(null);
    setFlyinReassignMemberId("");
    setFlyinReassignMemberQuery("");
    setIsFlyinReassignDropdownOpen(false);
    setReassignFlyinCustomerError(null);
    setIsReassigningFlyinCustomer(false);
  };

  const resetDeleteMemberFlow = () => {
    setMemberPendingRemoval(null);
    setIsDeleteMemberOpen(false);
    setIsManageMemberCustomersOpen(false);
    setIsReassignCustomersOpen(false);
    setBulkReassignmentMemberId("");
    setBulkReassignmentMemberQuery("");
    setIsBulkReassignmentDropdownOpen(false);
    setSelectedBusinessesForReassignment([]);
    setManagedCustomersQuery("");
    setSelectedReassignmentMemberId("");
    setSelectedReassignmentMemberQuery("");
    setIsSelectedReassignmentDropdownOpen(false);
    setReassignedCustomersById({});
    setDeleteMemberError(null);
    setReassignDeletionCustomersError(null);
    setIsDeletingMember(false);
    setIsReassigningDeletionCustomers(false);
  };

  const openRemoveMemberFlow = useCallback((member: Member) => {
    setMemberPendingRemoval(member);
    setIsDeleteMemberOpen(true);
    setIsManageMemberCustomersOpen(false);
    setIsReassignCustomersOpen(false);
    setBulkReassignmentMemberId("");
    setBulkReassignmentMemberQuery("");
    setIsBulkReassignmentDropdownOpen(false);
    setSelectedBusinessesForReassignment(
      member.businesses.map((business) => business.id),
    );
    setManagedCustomersQuery("");
    setSelectedReassignmentMemberId("");
    setSelectedReassignmentMemberQuery("");
    setIsSelectedReassignmentDropdownOpen(false);
    setReassignedCustomersById({});
    setDeleteMemberError(null);
    setReassignDeletionCustomersError(null);
    setIsDeletingMember(false);
    setIsReassigningDeletionCustomers(false);
    setIsMemberFlyinOpen(false);
  }, []);

  const toggleBusinessForReassignment = (businessId: string) => {
    setSelectedBusinessesForReassignment((prev) =>
      prev.includes(businessId)
        ? prev.filter((item) => item !== businessId)
        : [...prev, businessId],
    );
  };

  const openManageCustomersForReassignment = () => {
    if (!memberPendingRemoval) return;
    setBulkReassignmentMemberId("");
    setBulkReassignmentMemberQuery("");
    setIsBulkReassignmentDropdownOpen(false);
    const unassignedIds =
      remainingPendingRemovalBusinessIds.length > 0
        ? remainingPendingRemovalBusinessIds
        : memberPendingRemoval.businesses.map((business) => business.id);
    setSelectedBusinessesForReassignment(unassignedIds);
    setManagedCustomersQuery("");
    setIsDeleteMemberOpen(false);
    setIsManageMemberCustomersOpen(true);
  };

  const openReassignSelectedCustomers = () => {
    if (selectedBusinessesForReassignment.length === 0) return;
    setSelectedReassignmentMemberId("");
    setSelectedReassignmentMemberQuery("");
    setIsSelectedReassignmentDropdownOpen(false);
    setIsManageMemberCustomersOpen(false);
    setIsReassignCustomersOpen(true);
  };

  const saveSelectedCustomerReassignment = async () => {
    if (
      !memberPendingRemoval ||
      !selectedReassignmentMemberId ||
      selectedBusinessesForReassignment.length === 0 ||
      isReassigningDeletionCustomers
    ) {
      return;
    }

    setReassignDeletionCustomersError(null);
    setIsReassigningDeletionCustomers(true);
    try {
      await reassignCustomers({
        fromAdminId: memberPendingRemoval.id,
        toAdminId: selectedReassignmentMemberId,
        businessIds: selectedBusinessesForReassignment,
      });

      setReassignedCustomersById((prev) => {
        const next = { ...prev };
        selectedBusinessesForReassignment.forEach((businessId) => {
          next[businessId] = selectedReassignmentMemberId;
        });
        return next;
      });
      setSelectedBusinessesForReassignment([]);
      setSelectedReassignmentMemberId("");
      setSelectedReassignmentMemberQuery("");
      setIsSelectedReassignmentDropdownOpen(false);
      setIsReassignCustomersOpen(false);
      setIsDeleteMemberOpen(true);
      await refetchMembers();
    } catch (error) {
      const message = getReassignCustomersErrorMessage(error);
      setReassignDeletionCustomersError(message);
      showErrorToast(message);
    } finally {
      setIsReassigningDeletionCustomers(false);
    }
  };

  const handleSaveFlyinCustomerReassignment = async () => {
    if (
      !activeMember ||
      !flyinBusinessPendingReassign ||
      !flyinReassignMemberId ||
      isReassigningFlyinCustomer
    ) {
      return;
    }

    setReassignFlyinCustomerError(null);
    setIsReassigningFlyinCustomer(true);
    try {
      await reassignCustomers({
        fromAdminId: activeMember.id,
        toAdminId: flyinReassignMemberId,
        businessIds: [flyinBusinessPendingReassign.id],
      });

      setActiveMember((prev) =>
        prev
          ? {
              ...prev,
              businesses: prev.businesses.filter(
                (business) => business.id !== flyinBusinessPendingReassign.id,
              ),
            }
          : prev,
      );
      await refetchMembers();
      resetFlyinCustomerReassignFlow();
      setIsMemberFlyinOpen(true);
      showTeamManagementSuccessToast("Account manager changed successfully!");
    } catch (error) {
      const message = getReassignCustomersErrorMessage(error);
      setReassignFlyinCustomerError(message);
      showErrorToast(message);
    } finally {
      setIsReassigningFlyinCustomer(false);
    }
  };

  const handleDeleteMember = async () => {
    if (
      !memberPendingRemoval ||
      !canDeletePendingMember ||
      isDeletingMember ||
      isReassigningDeletionCustomers
    ) {
      return;
    }

    setDeleteMemberError(null);
    setIsDeletingMember(true);
    try {
      if (
        bulkReassignmentMemberId &&
        remainingPendingRemovalBusinessIds.length > 0
      ) {
        await reassignCustomers({
          fromAdminId: memberPendingRemoval.id,
          toAdminId: bulkReassignmentMemberId,
          businessIds: remainingPendingRemovalBusinessIds,
        });
      }

      await deleteAdminProfile(memberPendingRemoval.id);

      setRemovedMemberIds((prev) =>
        prev.includes(memberPendingRemoval.id)
          ? prev
          : [...prev, memberPendingRemoval.id],
      );

      if (activeMember?.id === memberPendingRemoval.id) {
        setActiveMember(null);
        setIsMemberFlyinOpen(false);
      }

      resetDeleteMemberFlow();
      await refetchMembers();
      showTeamManagementSuccessToast("Member removed successfully!");
    } catch (error) {
      const message = getDeleteMemberErrorMessage(error);
      setDeleteMemberError(message);
      showErrorToast(message);
    } finally {
      setIsDeletingMember(false);
    }
  };

  const toggleManagerAssignBusiness = (businessId: string) => {
    setSelectedBusinessIdsForManagerAssign((prev) =>
      prev.includes(businessId)
        ? prev.filter((item) => item !== businessId)
        : [...prev, businessId],
    );
  };

  const handleSaveMemberRoles = async () => {
    if (
      !activeMember ||
      isSavingMemberRoles ||
      editingMemberRoleIds.length === 0
    ) {
      return;
    }

    setSaveMemberRolesError(null);
    setIsSavingMemberRoles(true);
    try {
      await updateAdminRoleAndPermissions(activeMember.id, {
        roleIds: editingMemberRoleIds,
        additionalPermissions: activeMember.permissions,
      });

      setIsAssignRoleOpen(false);
      refetchMembers();
      showTeamManagementSuccessToast("Member roles updated successfully!");
    } catch (error) {
      const message = getSaveMemberRolesErrorMessage(error);
      setSaveMemberRolesError(message);
      showErrorToast(message);
    } finally {
      setIsSavingMemberRoles(false);
    }
  };

  const handleAssignCustomers = async () => {
    if (
      !activeMember ||
      isAssigningCustomers ||
      selectedBusinessIdsForManagerAssign.length === 0
    ) {
      return;
    }

    setAssignCustomersError(null);
    setIsAssigningCustomers(true);
    try {
      await assignCustomersToAdmin({
        adminId: activeMember.id,
        businessIds: selectedBusinessIdsForManagerAssign,
      });

      const selectedBusinesses = assignableBusinesses.filter((business) =>
        selectedBusinessIdsForManagerAssign.includes(business.id),
      );

      setActiveMember((prev) =>
        prev
          ? {
              ...prev,
              businesses: Array.from(
                new Map(
                  [
                    ...prev.businesses,
                    ...selectedBusinesses.map((business) => ({
                      id: business.id,
                      name: business.name,
                      email: business.email ?? business.user?.email,
                    })),
                  ].map((business) => [business.id, business]),
                ).values(),
              ),
            }
          : prev,
      );
      setIsAssignAccountManagerOpen(false);
      setIsMemberFlyinOpen(true);
      refetchMembers();
      showTeamManagementSuccessToast("Customers assigned successfully!");
    } catch (error) {
      const message = getAssignCustomersErrorMessage(error);
      setAssignCustomersError(message);
      showErrorToast(message);
    } finally {
      setIsAssigningCustomers(false);
    }
  };

  const toggleMemberPermission = (permission: string) => {
    setEditingMemberPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((item) => item !== permission)
        : [...prev, permission],
    );
  };

  const toggleMemberPermissionGroup = (groupPermissions: string[]) => {
    const isAllSelected = groupPermissions.every((permission) =>
      editingMemberPermissions.includes(permission),
    );

    if (isAllSelected) {
      setEditingMemberPermissions((prev) =>
        prev.filter((permission) => !groupPermissions.includes(permission)),
      );
      return;
    }

    setEditingMemberPermissions((prev) => {
      const next = new Set(prev);
      groupPermissions.forEach((permission) => next.add(permission));
      return Array.from(next);
    });
  };

  const handleSaveMemberPermissions = async () => {
    if (!activeMember || isSavingMemberPermissions) return;

    setSaveMemberPermissionsError(null);
    setIsSavingMemberPermissions(true);
    try {
      await updateAdminRoleAndPermissions(activeMember.id, {
        additionalPermissions: editingMemberPermissions,
      });

      setActiveMember((prev) =>
        prev
          ? {
              ...prev,
              permissions: editingMemberPermissions,
            }
          : prev,
      );
      setIsMemberPermissionsModalOpen(false);
      refetchMembers();
      showTeamManagementSuccessToast("Member permissions updated successfully!");
    } catch (error) {
      const message = getSaveMemberPermissionsErrorMessage(error);
      setSaveMemberPermissionsError(message);
      showErrorToast(message);
    } finally {
      setIsSavingMemberPermissions(false);
    }
  };

  const handleAddEmail = (value: string) => {
    const next = value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (next.length === 0) return;
    setEmails((prev) => Array.from(new Set([...prev, ...next])));
  };

  const removeEmail = (value: string) => {
    setEmails((prev) => prev.filter((email) => email !== value));
  };

  const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleAddEmail(emailInput);
      setEmailInput("");
    }
  };

  const columns = useMemo(
    () =>
      buildMemberColumns({
        onOpenMember: openMemberFlyin,
        onAssignAccountManager: openAssignAccountManagerForMember,
        onManageMember: openMemberFlyin,
        onEditRoles: openEditRolesForMember,
        onAddPermissions: openAddPermissionsForMember,
        onResendInvite: handleResendInvite,
        onRemoveMember: openRemoveMemberFlow,
      }),
    [
      openAssignAccountManagerForMember,
      openEditRolesForMember,
      openMemberFlyin,
      openAddPermissionsForMember,
      handleResendInvite,
      openRemoveMemberFlow,
    ],
  );

  return {
    query,
    setQuery,
    sort,
    setSort,
    roleTypes,
    setRoleTypes,
    sortOptions,
    roleTypeOptions,
    rolesForUi,
    columns,
    totalMembersCount: members.length,
    pagedMembers,
    filteredMembers,
    isMembersLoading,
    currentPage,
    setCurrentPage,
    totalMemberPages,
    openMemberFlyin,
    openInviteModal,
    isInviteOpen,
    setIsInviteOpen,
    emailInput,
    setEmailInput,
    handleEmailKeyDown,
    handleAddEmail,
    emails,
    removeEmail,
    selectedRoles,
    setSelectedRoles,
    setIsRolesModalOpen,
    isRolesModalOpen,
    selectedPermissionsForInvite,
    setExpandedGroupId,
    setIsPermissionsModalOpen,
    isPermissionsModalOpen,
    inviteError,
    setInviteError,
    handleInviteMembers,
    canInviteMembers,
    isInviting,
    permissionQuery,
    setPermissionQuery,
    filteredPermissionGroups,
    isPermissionsLoading,
    permissionsError,
    expandedGroupId,
    setSelectedAdditionalPermissions,
    toggleInvitePermission,
    toggleInviteGroupPermissions,
    rolePermissions,
    selectedAdditionalPermissions,
    toggleRoleSelection,
    isMemberPermissionsModalOpen,
    setIsMemberPermissionsModalOpen,
    isSavingMemberPermissions,
    saveMemberPermissionsError,
    setSaveMemberPermissionsError,
    activeMember,
    memberPermissionsQuery,
    setMemberPermissionsQuery,
    filteredPermissionGroupsForMember,
    memberExpandedPermissionGroupId,
    setMemberExpandedPermissionGroupId,
    editingMemberPermissions,
    toggleMemberPermission,
    toggleMemberPermissionGroup,
    handleSaveMemberPermissions,
    isMemberFlyinOpen,
    setIsMemberFlyinOpen,
    openAssignAccountManagerForMember,
    handleResendInvite,
    openEditRolesForMember,
    openAddPermissionsForMember,
    openRemoveMemberFlow,
    openChangeCustomerAccountManager,
    isAssignRoleOpen,
    setIsAssignRoleOpen,
    rolesForAssignRoleModal,
    editingMemberRoles,
    toggleEditingMemberRole,
    handleSaveMemberRoles,
    isSavingMemberRoles,
    saveMemberRolesError,
    setSaveMemberRolesError,
    isAssignAccountManagerOpen,
    setIsAssignAccountManagerOpen,
    unassignedBusinessesForActiveMember,
    selectedBusinessIdsForManagerAssign,
    toggleManagerAssignBusiness,
    isAssigningCustomers,
    assignCustomersError,
    setAssignCustomersError,
    handleAssignCustomers,
    setSelectedBusinessIdsForManagerAssign,
    isDeleteMemberOpen,
    setIsDeleteMemberOpen,
    isDeletingMember,
    isReassigningDeletionCustomers,
    resetDeleteMemberFlow,
    memberPendingRemoval,
    remainingPendingRemovalCustomersCount,
    canDeletePendingMember,
    bulkReassignmentMemberId,
    memberOptionsForReassignment,
    bulkReassignmentMemberQuery,
    isBulkReassignmentDropdownOpen,
    setIsBulkReassignmentDropdownOpen,
    setBulkReassignmentMemberQuery,
    setReassignDeletionCustomersError,
    setBulkReassignmentMemberId,
    openManageCustomersForReassignment,
    handleDeleteMember,
    reassignDeletionCustomersError,
    deleteMemberError,
    isManageMemberCustomersOpen,
    setIsManageMemberCustomersOpen,
    pendingRemovalBusinesses,
    selectedBusinessesForReassignment,
    managedCustomersQuery,
    setManagedCustomersQuery,
    toggleBusinessForReassignment,
    openReassignSelectedCustomers,
    isReassignCustomersOpen,
    setIsReassignCustomersOpen,
    selectedReassignmentMemberId,
    memberOptionsForReassignmentList: memberOptionsForReassignment,
    selectedReassignmentMemberQuery,
    isSelectedReassignmentDropdownOpen,
    setIsSelectedReassignmentDropdownOpen,
    setSelectedReassignmentMemberQuery,
    setSelectedReassignmentMemberId,
    saveSelectedCustomerReassignment,
    isFlyinCustomerReassignOpen,
    setIsFlyinCustomerReassignOpen,
    isReassigningFlyinCustomer,
    flyinReassignMemberId,
    memberOptionsForFlyinReassignment,
    flyinReassignMemberQuery,
    isFlyinReassignDropdownOpen,
    setIsFlyinReassignDropdownOpen,
    setFlyinReassignMemberQuery,
    setFlyinReassignMemberId,
    resetFlyinCustomerReassignFlow,
    handleSaveFlyinCustomerReassignment,
    reassignFlyinCustomerError,
  };
};
